/**
 * The HTTP response headers a download depends on.
 *
 * `Content-Type` and `Content-Disposition` are protocol text with their own
 * grammar — parameters after a semicolon, three spellings of `filename` — so
 * they are read here and returned as values. A server-supplied name still
 * reaches the user's disk, so path separators and control characters are
 * refused rather than sanitised away.
 */

const UTF8_FILENAME = /filename\*=UTF-8''([^;]+)/i;
const QUOTED_FILENAME = /filename="([^"]+)"/i;
const PLAIN_FILENAME = /filename=([^;\s]+)/i;

/** The media type, without its parameters. */
export function mediaType(contentType: string | null): string | null {
  return contentType?.split(";", 1)[0] ?? null;
}

/** The filename a Content-Disposition names, or null when it is unsafe. */
export function downloadFilename(
  disposition: string,
  extension: string,
): string | null {
  const encoded = disposition.match(UTF8_FILENAME)?.[1];
  const quoted = disposition.match(QUOTED_FILENAME)?.[1];
  const plain = disposition.match(PLAIN_FILENAME)?.[1];
  const candidate = encoded ? decodeURIComponent(encoded) : (quoted ?? plain);
  return isSafeFilename(candidate, extension) ? candidate : null;
}

function isSafeFilename(
  value: string | undefined,
  extension: string,
): value is string {
  if (!value?.toLowerCase().endsWith(extension)) return false;
  return [...value].every((character) => {
    const code = character.charCodeAt(0);
    return (
      character !== "/" && character !== "\\" && code >= 32 && code !== 127
    );
  });
}
